import React, { useState } from 'react';
import { Flashbar, FlashbarProps, Button } from '@amzn/awsui-components-console';
import './NotificationBanner.scss';

export type NotificationBannerVariant = 'success' | 'warning' | 'error' | 'info';

export interface NotificationBannerProps {
  variant: NotificationBannerVariant;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  dismissible?: boolean;
  onDismiss?: () => void;
}

/**
 * NotificationBanner - A full-width banner for important status changes
 * 
 * Variants:
 * - success: Green banner with checkmark icon (e.g., "Request submitted!")
 * - warning: Amber banner with warning icon (e.g., "Limited access")
 * - error: Red banner with X icon (e.g., "Failed to check access")
 * - info: Blue banner with info icon (e.g., "Access approved")
 * 
 * Use cases:
 * - After submitting access requests
 * - When access is approved
 * - On errors
 * - For important status updates
 */
const NotificationBanner: React.FC<NotificationBannerProps> = ({
  variant,
  title,
  description,
  action,
  dismissible = true,
  onDismiss
}) => {
  const [visible, setVisible] = useState(true);

  const handleDismiss = () => {
    setVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (!visible) {
    return null;
  }

  const items: FlashbarProps.MessageDefinition[] = [
    {
      type: variant,
      header: title,
      content: description,
      dismissible: dismissible,
      onDismiss: handleDismiss,
      ...(action && {
        action: (
          <Button
            onClick={action.onClick}
            variant="link"
          >
            {action.label}
          </Button>
        )
      })
    }
  ];

  return (
    <div className="notification-banner-wrapper">
      <Flashbar items={items} />
    </div>
  );
};

export default NotificationBanner;
